import { useEffect, useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
  cn,
} from '@design-system';

// Which clients the advisor works with this partner on. 'all' auto-shares future clients (existing
// clients are still shared one at a time from the Clients table); 'specific' shares nothing
// automatically — every client is shared per-case via Share with Supervisor.
export type PartnerClientScope = 'all' | 'specific';

interface PartnerConnectElectionDialogProps {
  open: boolean;
  isConnecting: boolean;
  onConfirm: (scope: PartnerClientScope) => void;
  onCancel: () => void;
}

// The Edward Jones connect election. The advisor is stating, as a matter of fact, which distribution
// partner they work with — not directing or authorizing a share. Waterlily acts on that election
// under its own client agreement. Confirming establishes the connection(s).
export function PartnerConnectElectionDialog({
  open,
  isConnecting,
  onConfirm,
  onCancel,
}: PartnerConnectElectionDialogProps) {
  const [scope, setScope] = useState<PartnerClientScope>('all');

  useEffect(() => {
    if (open) setScope('all');
  }, [open]);

  const options: { value: PartnerClientScope; title: string; description: string }[] = [
    {
      value: 'all',
      title: 'I work with this partner for all my clients',
      description:
        'Waterlily will share all newly invited clients with this distribution partner to aid collaboration. Existing clients can be connected one-by-one from your dashboard.',
    },
    {
      value: 'specific',
      title: 'Only for specific clients',
      description:
        'You’ll be able to connect specific clients to this partner from your dashboard, and Waterlily will share their data to support collaboration.',
    },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => !isOpen && !isConnecting && onCancel()}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-primary-dark">
            How do you work with this partner?
          </DialogTitle>
          <DialogDescription className="text-foreground">
            Collaborate in Waterlily the way you do in real life. Waterlily may share client data
            with your partner(s) to support collaboration, always in accordance with the consents
            your clients accept in Waterlily.
          </DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={scope}
          onValueChange={value => setScope(value as PartnerClientScope)}
          className="gap-3"
        >
          {options.map(option => (
            <Label
              key={option.value}
              htmlFor={`partner-scope-${option.value}`}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-md border p-4 transition-colors',
                { 'border-primary bg-primary/5': scope === option.value },
              )}
            >
              <RadioGroupItem
                id={`partner-scope-${option.value}`}
                value={option.value}
                className="mt-0.5"
              />
              <span className="flex flex-col gap-1">
                <span className="text-foreground text-base font-medium">{option.title}</span>
                <span className="text-muted-foreground text-sm">{option.description}</span>
              </span>
            </Label>
          ))}
        </RadioGroup>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isConnecting}
          >
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(scope)}
            disabled={isConnecting}
          >
            {isConnecting ? 'Connecting…' : 'Connect'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
